/**
 * The Consolidation route table (CLAUDE.md §8.16).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `CONSOL_ROUTES` in.
 *
 * Two shapes here are load-bearing:
 *
 *  - **A run's five surfaces are five routes, not five tabs in one.** The
 *    overview, the consolidated trial balance, the income statement, the balance
 *    sheet, the elimination proposals and the mismatch report are each a
 *    different question, each worth its own URL to send to somebody, and each is
 *    a figure that ends up in a board pack. `RunTabs.vue` renders the strip; the
 *    router owns the truth.
 *  - **Every `:id` is constrained to `\d+`**, so `/consol/runs/new` could never
 *    be parsed as run number "new". There is deliberately no such route: a run
 *    is a group and two dates, and generating one from a dialog on the list
 *    keeps the previous run — the thing you are about to compare against —
 *    visible, which is what §11's "never lose page context" asks for.
 *
 * Everything except the export button is gated on a read permission.
 * `consol.run.generate`, `consol.group.manage`, `consol.elimination.decide` and
 * `consol.report.export` are checked on the **action**, never on the route: an
 * Auditor holds `consol.group.read` and `consol.run.read` and nothing else, and
 * must be able to open every one of these screens and change none of them.
 */

import type { RouteRecordRaw } from 'vue-router'

import { CONSOL_ACCOUNT_MAP_ROUTES } from '@/modules/consol/account-maps/routes'
import { CONSOL_ADJUSTMENT_ROUTES } from '@/modules/consol/adjustments/routes'
import { CONSOL_PERMISSIONS } from '@/modules/consol/permissions'
import { CONSOL_READINESS_ROUTES } from '@/modules/consol/readiness/routes'
import { CONSOL_STATEMENT_ROUTES } from '@/modules/consol/statements/routes'

export const CONSOL_ROUTES: RouteRecordRaw[] = [
  /* FT11-UC (FSD 2.2.7 V01, V02): readiness for a period, and the account map. */
  ...CONSOL_READINESS_ROUTES,
  ...CONSOL_ACCOUNT_MAP_ROUTES,
  /* FT14-U26 (FSD 2.2.7 V06, 2.2.7.10): group adjustments, and the statement pack and analyses
   * of a run. */
  ...CONSOL_ADJUSTMENT_ROUTES,
  ...CONSOL_STATEMENT_ROUTES,

  {
    path: '/consol/groups',
    name: 'consol-groups',
    component: () => import('@/modules/consol/GroupsListView.vue'),
    meta: { titleKey: 'consol.routeTitles.groups', permission: CONSOL_PERMISSIONS.groupRead },
  },
  {
    path: '/consol/groups/:id(\\d+)',
    name: 'consol-group',
    component: () => import('@/modules/consol/GroupView.vue'),
    meta: { titleKey: 'consol.routeTitles.group', permission: CONSOL_PERMISSIONS.groupRead },
  },

  {
    path: '/consol/runs',
    name: 'consol-runs',
    component: () => import('@/modules/consol/RunsListView.vue'),
    meta: { titleKey: 'consol.routeTitles.runs', permission: CONSOL_PERMISSIONS.runRead },
  },
  {
    path: '/consol/runs/:id(\\d+)',
    name: 'consol-run',
    component: () => import('@/modules/consol/RunView.vue'),
    meta: { titleKey: 'consol.routeTitles.run', permission: CONSOL_PERMISSIONS.runRead },
  },
  {
    path: '/consol/runs/:id(\\d+)/trial-balance',
    name: 'consol-run-trial-balance',
    component: () => import('@/modules/consol/TrialBalanceView.vue'),
    meta: { titleKey: 'consol.routeTitles.trialBalance', permission: CONSOL_PERMISSIONS.runRead },
  },
  {
    path: '/consol/runs/:id(\\d+)/income-statement',
    name: 'consol-run-income-statement',
    component: () => import('@/modules/consol/IncomeStatementView.vue'),
    meta: {
      titleKey: 'consol.routeTitles.incomeStatement',
      permission: CONSOL_PERMISSIONS.runRead,
    },
  },
  {
    path: '/consol/runs/:id(\\d+)/balance-sheet',
    name: 'consol-run-balance-sheet',
    component: () => import('@/modules/consol/BalanceSheetView.vue'),
    meta: { titleKey: 'consol.routeTitles.balanceSheet', permission: CONSOL_PERMISSIONS.runRead },
  },
  {
    path: '/consol/runs/:id(\\d+)/eliminations',
    name: 'consol-run-eliminations',
    component: () => import('@/modules/consol/EliminationsView.vue'),
    meta: { titleKey: 'consol.routeTitles.eliminations', permission: CONSOL_PERMISSIONS.runRead },
  },
  {
    path: '/consol/runs/:id(\\d+)/mismatches',
    name: 'consol-run-mismatches',
    component: () => import('@/modules/consol/MismatchReportView.vue'),
    meta: { titleKey: 'consol.routeTitles.mismatches', permission: CONSOL_PERMISSIONS.runRead },
  },
]

/* ------------------------------------------------------------ own screens */

export const CONSOL_PATHS = {
  groups: '/consol/groups',
  group: (id: number) => `/consol/groups/${id}`,
  runs: '/consol/runs',
  runsForGroup: (groupId: number) => `/consol/runs?groupId=${groupId}`,
  run: (id: number) => `/consol/runs/${id}`,
  trialBalance: (id: number) => `/consol/runs/${id}/trial-balance`,
  incomeStatement: (id: number) => `/consol/runs/${id}/income-statement`,
  balanceSheet: (id: number) => `/consol/runs/${id}/balance-sheet`,
  eliminations: (id: number) => `/consol/runs/${id}/eliminations`,
  mismatches: (id: number) => `/consol/runs/${id}/mismatches`,
  readiness: (scope: { groupId?: number | null; periodId?: number | null; fiscalYear?: number | null } = {}) =>
    withQuery('/consol/readiness', scope),
  accountMaps: (
    scope: {
      groupId?: number | null
      companyId?: number | null
      asOf?: string | null
      /** The reporting period whose readiness supplies the accounts that carry a balance. */
      periodId?: number | null
      /** 1: unmapped accounts with a balance (when a period is given); 'all': every unmapped account. */
      unmapped?: 1 | 'all' | null
      /** One local account to bring into view. */
      account?: number | null
    } = {},
  ) => withQuery('/consol/account-maps', scope),
} as const

function withQuery(path: string, scope: Record<string, number | string | null | undefined>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(scope)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const query = params.toString()
  return query === '' ? path : `${path}?${query}`
}
